/**
 * 層1: ドラフトバッファ（IndexedDB）。docs/02-architecture.md §5。
 *
 * 不変条件: 入力済みのテキストは、いかなる障害でも失われない。
 * ここはその最初の防壁で、**ネットワークに一切依存しない**。入力のたびに
 * （composition 中も含めて）端末内へ書き、サーバー保存が成功するまで消さない。
 *
 * composition 中も書いてよい理由: クラッシュ時に変換途中まで復元できる方が良い。
 * サーバーへ「確定本文として送らない」制約は層2の責務であって、ここではない。
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { JstDate, Note } from '@shared/types';

const DB_NAME = 'mamemo6';
const DB_VERSION = 1;
const DRAFT_STORE = 'drafts';

/** 最初の POST を、応答・プロセス喪失後にも同じ内容とキーで再現する記録。 */
export interface DraftCreateAttempt {
  title: string | null;
  body: string;
  category_id: number | null;
  subject_date: JstDate;
  subject_date_end: JstDate | null;
  session_id: string;
  client_request_id: string;
}

export interface DraftRecord {
  /** サーバー上のノート ID、または未送信の新規ノートのローカル一時 ID。 */
  note_id: string;
  title: string | null;
  body: string;
  category_id: number | null;
  subject_date: JstDate;
  subject_date_end: JstDate | null;
  /** このドラフトの基になったサーバー版番号。復元判定に使う。 */
  base_version: number;
  /** このドラフトを書いた執筆セッション。 */
  session_id: string;
  /** 端末内へ書いた時刻（ISO 8601 UTC）。 */
  saved_at: string;
  /** サーバーへ未反映の内容を含むか。false ならヘッドと一致している。 */
  dirty: boolean;
  /** サーバー上にまだ実体がない（POST 前の）新規ノートか。 */
  local_only: boolean;
  /** 初回 POST の不変 payload。local→server ID 移行完了時だけ除去する。 */
  create_attempt?: DraftCreateAttempt;
}

export type DraftContent = Pick<
  DraftRecord,
  'title' | 'body' | 'category_id' | 'subject_date' | 'subject_date_end'
>;

/** POST / PUT 応答の実サーバー内容を、ドラフトの同期判定へ使う形にする。 */
export function noteToDraftContent(
  note: Pick<
    Note,
    'title' | 'body' | 'category_id' | 'subject_date' | 'subject_date_end'
  >,
): DraftContent {
  return {
    title: note.title,
    body: note.body,
    category_id: note.category_id,
    subject_date: note.subject_date,
    subject_date_end: note.subject_date_end,
  };
}

interface Mamemo6DB extends DBSchema {
  [DRAFT_STORE]: {
    key: string;
    value: DraftRecord;
    indexes: { dirty_saved_at: string };
  };
}

let dbPromise: Promise<IDBPDatabase<Mamemo6DB>> | null = null;
/**
 * IndexedDB の debounce 前でも、同じ SPA 内の閲覧画面が最新入力を読めるようにする短命キャッシュ。
 * 永続化の代わりではなく、書き込み完了までの競争を埋める層としてだけ使う。
 */
const liveDrafts = new Map<string, DraftRecord>();

function getDb(): Promise<IDBPDatabase<Mamemo6DB>> {
  dbPromise ??= openDB<Mamemo6DB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const store = db.createObjectStore(DRAFT_STORE, { keyPath: 'note_id' });
      // 未送信ドラフトの掃き出しを古い順に処理するための索引。
      store.createIndex('dirty_saved_at', 'saved_at');
    },
  });
  return dbPromise;
}

/** 新規ノート用のローカル一時 ID。サーバー ID（ULID）と衝突しない接頭辞を付ける。 */
export function newLocalNoteId(): string {
  return `local:${crypto.randomUUID()}`;
}

export function isLocalNoteId(id: string): boolean {
  return id.startsWith('local:');
}

/** 入力のたびに同期的に呼び、画面遷移直後の読み取りへ最新値を渡す。 */
export function stageDraft(draft: DraftRecord): void {
  liveDrafts.set(draft.note_id, preserveCreateAttempt(draft, liveDrafts.get(draft.note_id)));
}

export async function putDraft(draft: DraftRecord): Promise<DraftRecord> {
  const staged = preserveCreateAttempt(draft, liveDrafts.get(draft.note_id));
  liveDrafts.set(draft.note_id, staged);
  const db = await getDb();
  const tx = db.transaction(DRAFT_STORE, 'readwrite');
  const stored = await tx.store.get(draft.note_id);
  const next = preserveCreateAttempt(staged, stored);
  await tx.store.put(next);
  await tx.done;
  // IndexedDB待機中に追加入力がstageされたなら、その新しいlive値を古いputで戻さない。
  if (liveDrafts.get(draft.note_id) === staged) liveDrafts.set(draft.note_id, next);
  return next;
}

export type AttachDraftCreateAttemptResult =
  | { kind: 'attached' | 'existing'; draft: DraftRecord }
  | { kind: 'conflict' };

/**
 * 回収開始時の内容が現在値のままの場合だけ、初回POST markerを付与する。
 *
 * 汎用putDraftと違い、古い回収snapshotで別タブの新しいstored内容を上書きしない。
 * 既にmarkerがある場合は、その不変payloadと最新内容をそのまま呼び出し元へ返す。
 */
export async function attachDraftCreateAttemptIfUnchanged(
  expected: DraftRecord,
  createAttempt: DraftCreateAttempt,
): Promise<AttachDraftCreateAttemptResult> {
  const db = await getDb();
  const tx = db.transaction(DRAFT_STORE, 'readwrite');
  const stored = await tx.store.get(expected.note_id);
  const liveAtRead = liveDrafts.get(expected.note_id);
  const current = selectDraftForRead(liveAtRead, stored);

  if (!current) {
    await tx.done;
    return { kind: 'conflict' };
  }
  if (stored?.create_attempt) {
    await tx.done;
    if (liveDrafts.get(expected.note_id) !== liveAtRead) return { kind: 'conflict' };
    return { kind: 'existing', draft: current };
  }
  if (!isSameDraftSnapshot(current, expected)) {
    await tx.done;
    return { kind: 'conflict' };
  }

  // liveにだけ先行markerがある場合も、proposedで上書きせずそのpayloadを永続化する。
  const next: DraftRecord = {
    ...current,
    create_attempt: current.create_attempt ?? createAttempt,
  };
  await tx.store.put(next);
  if (liveDrafts.get(expected.note_id) !== liveAtRead) {
    tx.abort();
    await tx.done.catch(() => undefined);
    return { kind: 'conflict' };
  }
  await tx.done;
  if (liveDrafts.get(expected.note_id) !== liveAtRead) return { kind: 'conflict' };
  liveDrafts.set(expected.note_id, next);
  return { kind: 'attached', draft: next };
}

export async function getDraft(noteId: string): Promise<DraftRecord | undefined> {
  const db = await getDb();
  const stored = await db.get(DRAFT_STORE, noteId);
  // DB 読み取り中に stageDraft が走る場合もあるので、live は await の後に読む。
  return selectDraftForRead(liveDrafts.get(noteId), stored);
}

export async function deleteDraft(noteId: string): Promise<void> {
  liveDrafts.delete(noteId);
  const db = await getDb();
  await db.delete(DRAFT_STORE, noteId);
}

/** 未送信の下書きを古い順に返す。起動時の復旧処理が使う。 */
export async function listDirtyDrafts(): Promise<DraftRecord[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex(DRAFT_STORE, 'dirty_saved_at');
  return all.filter((d) => d.dirty);
}

export async function listAllDrafts(): Promise<DraftRecord[]> {
  const db = await getDb();
  return db.getAll(DRAFT_STORE);
}

/** 回収開始時のスナップショットと、適用直前のドラフトが同一かを厳密に判定する。 */
export function isSameDraftSnapshot(a: DraftRecord, b: DraftRecord): boolean {
  return (
    a.note_id === b.note_id &&
    isSameDraftContent(a, b) &&
    a.base_version === b.base_version &&
    a.session_id === b.session_id &&
    a.saved_at === b.saved_at &&
    a.dirty === b.dirty &&
    a.local_only === b.local_only &&
    isSameCreateAttempt(a.create_attempt, b.create_attempt)
  );
}

/**
 * POST 成功後に local / server ID の現在値を統合する純粋な計画。
 *
 * local 側の POST 後追加入力と、別タブが先に昇格して server 側へ書いた入力の
 * どちらも候補にする。一方だけが dirty なら dirty 側、同じ状態なら saved_at が
 * 新しい側を採用し、古い local 値で server 側の未送信入力を上書きしない。
 */
export interface DraftPromotionPlan {
  /** server ID 側へ残すドラフト。 */
  promoted: DraftRecord;
  /** local ID 側を削除する必要があるか。 */
  deleteSource: boolean;
}

/** 昇格 transaction の読み取り後に、両 ID の同一タブ live 値が変化していないか。 */
export function areDraftPromotionLiveCopiesUnchanged(
  sourceAtRead: DraftRecord | undefined,
  destinationAtRead: DraftRecord | undefined,
  sourceNow: DraftRecord | undefined,
  destinationNow: DraftRecord | undefined,
): boolean {
  return sourceNow === sourceAtRead && destinationNow === destinationAtRead;
}

export function planDraftPromotion(
  recovered: DraftRecord,
  sourceCurrent: DraftRecord | undefined,
  destinationCurrent: DraftRecord | undefined,
  serverId: string,
  serverVersion: number,
): DraftPromotionPlan | undefined {
  const source =
    sourceCurrent?.note_id === recovered.note_id
      ? {
          ...sourceCurrent,
          note_id: serverId,
          base_version: serverVersion,
          local_only: false,
          dirty: !isSameDraftContent(sourceCurrent, recovered),
        }
      : undefined;
  const destination =
    destinationCurrent?.note_id === serverId ? destinationCurrent : undefined;

  // local が無く server がある場合は、別タブまたは前回試行が既に昇格を完了している。
  if (!source) {
    if (!destination) return undefined;
    const { create_attempt: _completedAttempt, ...completedDestination } = destination;
    return { promoted: completedDestination, deleteSource: false };
  }

  // 引数順を destination, source とすることで、dirty / saved_at が同値なら既に
  // server ID を持つ側へ倒す。優先規則そのものは selectDraftForRead と同一。
  const promoted = selectDraftForRead(destination, source) ?? source;
  const { create_attempt: _completedAttempt, ...completedPromotion } = promoted;
  return {
    promoted: {
      ...completedPromotion,
      note_id: serverId,
      local_only: false,
    },
    deleteSource: true,
  };
}

/**
 * POST 済み local draft を 1 transaction で server ID へ昇格する。
 * transaction 中に同じタブの live 値が変化した場合は abort し、次の再試行へ残す。
 */
export async function promoteDraftAfterCreate(
  recovered: DraftRecord,
  serverId: string,
  serverVersion: number,
): Promise<boolean> {
  const db = await getDb();
  const tx = db.transaction(DRAFT_STORE, 'readwrite');
  const [sourceStored, destinationStored] = await Promise.all([
    tx.store.get(recovered.note_id),
    tx.store.get(serverId),
  ]);
  const sourceLiveAtRead = liveDrafts.get(recovered.note_id);
  const destinationLiveAtRead = liveDrafts.get(serverId);
  const sourceCurrent = selectDraftForRead(sourceLiveAtRead, sourceStored);
  const destinationCurrent = selectDraftForRead(destinationLiveAtRead, destinationStored);
  const plan = planDraftPromotion(
    recovered,
    sourceCurrent,
    destinationCurrent,
    serverId,
    serverVersion,
  );
  if (!plan) {
    await tx.done;
    return false;
  }

  await tx.store.put(plan.promoted);
  if (plan.deleteSource) await tx.store.delete(recovered.note_id);
  if (
    !areDraftPromotionLiveCopiesUnchanged(
      sourceLiveAtRead,
      destinationLiveAtRead,
      liveDrafts.get(recovered.note_id),
      liveDrafts.get(serverId),
    )
  ) {
    tx.abort();
    await tx.done.catch(() => undefined);
    return false;
  }
  await tx.done;
  if (
    !areDraftPromotionLiveCopiesUnchanged(
      sourceLiveAtRead,
      destinationLiveAtRead,
      liveDrafts.get(recovered.note_id),
      liveDrafts.get(serverId),
    )
  ) {
    return false;
  }
  if (plan.deleteSource) liveDrafts.delete(recovered.note_id);
  liveDrafts.set(serverId, plan.promoted);
  return true;
}

/** 回収した内容が現在値のままの場合だけ dirty=false を確定する。 */
export async function finalizeRecoveredDraft(
  recovered: DraftRecord,
  serverVersion: number,
): Promise<boolean> {
  const db = await getDb();
  const tx = db.transaction(DRAFT_STORE, 'readwrite');
  const stored = await tx.store.get(recovered.note_id);
  const liveAtRead = liveDrafts.get(recovered.note_id);
  const current = selectDraftForRead(liveAtRead, stored);
  if (!current || !isSameDraftSnapshot(current, recovered)) {
    await tx.done;
    return false;
  }

  const clean: DraftRecord = {
    ...current,
    base_version: serverVersion,
    dirty: false,
    local_only: false,
  };
  await tx.store.put(clean);
  if (liveDrafts.get(recovered.note_id) !== liveAtRead) {
    tx.abort();
    await tx.done.catch(() => undefined);
    return false;
  }
  await tx.done;
  if (liveDrafts.get(recovered.note_id) !== liveAtRead) return false;
  liveDrafts.set(recovered.note_id, clean);
  return true;
}

/** 回収開始時から変わっていない draft だけを削除する。 */
export async function deleteDraftIfUnchanged(recovered: DraftRecord): Promise<boolean> {
  const db = await getDb();
  const tx = db.transaction(DRAFT_STORE, 'readwrite');
  const stored = await tx.store.get(recovered.note_id);
  const liveAtRead = liveDrafts.get(recovered.note_id);
  const current = selectDraftForRead(liveAtRead, stored);
  if (!current || !isSameDraftSnapshot(current, recovered)) {
    await tx.done;
    return false;
  }

  await tx.store.delete(recovered.note_id);
  if (liveDrafts.get(recovered.note_id) !== liveAtRead) {
    tx.abort();
    await tx.done.catch(() => undefined);
    return false;
  }
  await tx.done;
  if (liveDrafts.get(recovered.note_id) !== liveAtRead) return false;
  liveDrafts.delete(recovered.note_id);
  return true;
}

/**
 * サーバー保存が成功した内容と一致するドラフトを「送信済み」にする。
 *
 * **ドラフトを削除せず dirty=false にするだけ**にしているのは、保存成功の直後に
 * タブが落ちても内容が端末に残っている方が安全なため。掃除は起動時に
 * `pruneCleanDrafts()` で行う（`@app/save/startup` の `runStartupMaintenance()`）。
 * 送信を開始した内容と、その後も更新され得る現在ドラフトの全編集フィールドを比較する。
 */
export function isSameDraftContent(draft: DraftContent, synced: DraftContent): boolean {
  return (
    draft.body === synced.body &&
    (draft.title ?? null) === (synced.title ?? null) &&
    draft.category_id === synced.category_id &&
    draft.subject_date === synced.subject_date &&
    (draft.subject_date_end ?? null) === (synced.subject_date_end ?? null)
  );
}

export async function markDraftSynced(
  noteId: string,
  version: number,
  synced: DraftContent,
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(DRAFT_STORE, 'readwrite');
  const existing = await tx.store.get(noteId);
  const liveAtRead = liveDrafts.get(noteId);
  const current = selectDraftForRead(liveAtRead, existing);
  if (current) {
    // 保存要求を出した後にさらに入力されている場合がある。その場合は dirty のまま残す。
    const stillDirty = !isSameDraftContent(current, synced);
    const next: DraftRecord = {
      ...current,
      base_version: version,
      dirty: stillDirty,
      local_only: false,
    };
    await tx.store.put(next);
    if (liveDrafts.get(noteId) !== liveAtRead) {
      tx.abort();
      await tx.done.catch(() => undefined);
      return;
    }
    await tx.done;
    if (liveDrafts.get(noteId) === liveAtRead) liveDrafts.set(noteId, next);
    return;
  }
  await tx.done;
}

/** 掃除の既定のしきい値（7 日）。 */
export const PRUNE_OLDER_THAN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * このドラフトを掃除してよいか（純粋関数）。
 *
 * **絶対規則2 の最前線。** ここが true を返した瞬間に端末上の本文が消えるので、
 * 判定は「サーバーへ送り終えている（`dirty === false`）」ことを必須にする。
 * 保存時刻が壊れている場合も消さない（判断できないものは残す側へ倒す）。
 */
export function shouldPruneDraft(draft: DraftRecord, cutoffMs: number): boolean {
  if (draft.dirty || draft.create_attempt) return false;
  const savedMs = Date.parse(draft.saved_at);
  if (Number.isNaN(savedMs)) return false;
  return savedMs < cutoffMs;
}

/**
 * このドラフトを今すぐ捨ててよいか（純粋関数）。
 *
 * サーバー側でヘッドが進んだ後（リビジョン復元など）に、**古いだけの**ドラフトを
 * 片付けるために使う。`dirty` なドラフト＝まだサーバーへ届いていない入力が
 * 残っているものは、絶対に捨てない（孤児として履歴へ退避する経路に任せる）。
 */
export function isDraftSafeToDiscard(draft: DraftRecord | undefined): boolean {
  if (!draft) return false;
  return !draft.dirty && !draft.create_attempt;
}

/**
 * 同じ note ID のメモリ値と IndexedDB 値を統合する。
 *
 * 別タブの未送信入力を隠さないことを最優先し、一方だけが dirty なら時刻に関係なく
 * dirty 側を返す。同じ状態同士なら saved_at が新しい方を返す。時刻が壊れている場合は、
 * 同じタブで最後に stage された live 側へ倒す。
 */
export function selectDraftForRead(
  live: DraftRecord | undefined,
  stored: DraftRecord | undefined,
): DraftRecord | undefined {
  if (!live) return stored;
  if (!stored) return live;
  let selected: DraftRecord;
  if (live.dirty !== stored.dirty) {
    selected = live.dirty ? live : stored;
  } else {
    const liveMs = Date.parse(live.saved_at);
    const storedMs = Date.parse(stored.saved_at);
    selected =
      !Number.isNaN(liveMs) && !Number.isNaN(storedMs) && liveMs !== storedMs
        ? liveMs > storedMs
          ? live
          : stored
        : live;
  }

  // 内容winnerとは独立してmarkerを統合する。永続済みstored markerを最優先し、
  // storedに無い場合もdebounce前のlive markerを落とさない。
  return preserveCreateAttempt(preserveCreateAttempt(selected, live), stored);
}

/** fallback の marker を、marker を持たないselectedへ補う。 */
export function preserveCreateAttempt(
  selected: DraftRecord,
  fallback: DraftRecord | undefined,
): DraftRecord {
  const createAttempt = fallback?.create_attempt ?? selected.create_attempt;
  if (createAttempt === selected.create_attempt) return selected;
  return { ...selected, create_attempt: createAttempt };
}

export function isSameCreateAttempt(
  a: DraftCreateAttempt | undefined,
  b: DraftCreateAttempt | undefined,
): boolean {
  if (!a || !b) return a === b;
  return (
    a.title === b.title &&
    a.body === b.body &&
    a.category_id === b.category_id &&
    a.subject_date === b.subject_date &&
    a.subject_date_end === b.subject_date_end &&
    a.session_id === b.session_id &&
    a.client_request_id === b.client_request_id
  );
}

/** どのコピーにも未送信入力が無いと確認できたときだけ削除を許す。 */
export function areDraftCopiesSafeToDiscard(
  live: DraftRecord | undefined,
  stored: DraftRecord | undefined,
): boolean {
  const copies = [live, stored].filter((draft): draft is DraftRecord => draft !== undefined);
  return copies.length > 0 && copies.every((draft) => !draft.dirty && !draft.create_attempt);
}

/** 同期済みかつ一定期間更新のないドラフトを掃除する。既定は 7 日。 */
export async function pruneCleanDrafts(olderThanMs = PRUNE_OLDER_THAN_MS): Promise<number> {
  const db = await getDb();
  const cutoff = Date.now() - olderThanMs;
  const tx = db.transaction(DRAFT_STORE, 'readwrite');
  let removed = 0;
  for (const draft of await tx.store.getAll()) {
    if (!shouldPruneDraft(draft, cutoff)) continue;
    await tx.store.delete(draft.note_id);
    removed++;
  }
  await tx.done;
  return removed;
}

/**
 * サーバーへ送信済みのドラフトだけを削除する。未送信分が残っていれば**何もしない**。
 *
 * リビジョン復元の直後に使う。復元でヘッドの版が進むため、送信済みのドラフトが
 * 次回オープン時に「孤児ドラフト」と判定され、身に覚えのない `draft_recovery` が
 * 履歴へ積まれてしまう（監査 A-7）。**未送信の入力がある場合は従来どおり退避させたい**
 * ので、判定は `isDraftSafeToDiscard` に委ねる。
 *
 * @returns 実際に削除したか
 */
export async function deleteDraftIfSynced(noteId: string): Promise<boolean> {
  const db = await getDb();
  const tx = db.transaction(DRAFT_STORE, 'readwrite');
  const existing = await tx.store.get(noteId);
  // 別タブが共有 IndexedDB へ dirty draft を書いている可能性がある。片方でも dirty なら
  // fail-safe で削除しない（メモリ値を無条件優先すると別タブの入力を消し得る）。
  const safe = areDraftCopiesSafeToDiscard(liveDrafts.get(noteId), existing);
  if (safe) {
    await tx.store.delete(noteId);
    liveDrafts.delete(noteId);
  }
  await tx.done;
  return safe;
}
